import React, { useState, useEffect } from 'react';
import api from '../api';
import { X, ShieldCheck, ChartPie, SpinnerGap, Database, UserCircle, Robot, ArrowRight, Warning, CheckCircle } from '@phosphor-icons/react';

function IntegrityGauge({ label, value, detail, color }) {
  const r = 28, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="flex items-center gap-3 p-3 bg-surface-default rounded-lg border border-border-subtle">
      <svg width="64" height="64" className="flex-shrink-0">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#F1F3F5" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform="rotate(-90 32 32)" className="transition-all duration-700" />
        <text x="32" y="32" textAnchor="middle" dominantBaseline="central"
          className="font-heading font-bold text-sm" fill="#1A1F1B">{Math.round(value)}%</text>
      </svg>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-content-primary">{label}</p>
        <p className="text-[9px] text-content-disabled mt-0.5 leading-relaxed">{detail}</p>
      </div>
    </div>
  );
}

function ProvenanceStep({ step, isLast }) {
  const iconMap = { 'Data Source': Database, 'AI Validation': Robot, 'Evidence': ShieldCheck };
  const Icon = iconMap[step.step] || UserCircle;
  const ok = step.status === 'verified' || step.status === 'completed';
  return (
    <div className="flex items-center gap-0 flex-shrink-0">
      <div className={`p-2.5 rounded-lg border min-w-[120px] ${ok ? 'bg-status-on-track-bg/50 border-status-on-track/20' : 'bg-status-watch-bg/50 border-status-watch/20'}`}>
        <div className="flex items-center gap-1 mb-1">
          <Icon size={11} className={step.step === 'AI Validation' ? 'text-brand-accent' : 'text-brand-primary'} />
          <span className="text-[10px] font-bold text-content-primary">{step.step}</span>
        </div>
        {step.actor && <p className="text-[9px] text-content-secondary">{step.actor}</p>}
        {step.detail && <p className="text-[9px] text-content-disabled">{step.detail}</p>}
        {step.timestamp && <p className="text-[8px] text-content-disabled font-mono mt-0.5">{new Date(step.timestamp).toLocaleDateString()}</p>}
        <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${ok ? 'bg-status-on-track/10 text-status-on-track' : 'bg-status-watch/10 text-status-watch'}`}>{step.status}</span>
      </div>
      {!isLast && <ArrowRight size={14} className="text-content-disabled mx-1 flex-shrink-0" />}
    </div>
  );
}

export default function DrillDownModal({ isOpen, onClose, metricKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !metricKey) return;
    setLoading(true);
    setData(null);
    api.post('/api/drill-down', { metric_key: metricKey })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [isOpen, metricKey]);

  if (!isOpen) return null;
  const { title = 'Drill-Down', breakdown = [], columns = [], provenance = {}, integrity = {} } = data || {};

  return (
    <div data-testid="drill-down-overlay" className="fixed inset-0 z-50 flex items-start justify-center pt-6 pb-6 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="drill-down-modal" className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[92vh] overflow-y-auto animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-border-default rounded-t-xl">
          <div>
            <p className="text-[10px] font-semibold text-brand-accent uppercase tracking-wider">Data Drill-Down</p>
            <h2 data-testid="drill-down-title" className="font-heading font-semibold text-lg text-content-primary">{loading ? 'Loading...' : title}</h2>
          </div>
          <button data-testid="drill-down-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-surface-muted flex items-center justify-center transition-colors">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={28} className="animate-spin text-brand-primary" />
            <span className="ml-3 text-sm text-content-secondary">Loading breakdown...</span>
          </div>
        ) : data ? (
          <div className="p-6 space-y-6">
            {/* AI Integrity Assessment */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Robot size={14} className="text-brand-accent" />
                <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider">AI Data Integrity Assessment</h3>
              </div>
              <div data-testid="integrity-section" className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <IntegrityGauge
                  label="Anomaly Score"
                  value={Math.max(0, 100 - (integrity.anomaly_pct || 0))}
                  detail={integrity.anomaly_details || 'No anomalies detected'}
                  color={(integrity.anomaly_pct || 0) > 20 ? '#C6493A' : (integrity.anomaly_pct || 0) > 10 ? '#D9943B' : '#2D7D46'}
                />
                <IntegrityGauge
                  label="Data Recency"
                  value={integrity.recency_score || 50}
                  detail={`${integrity.recency_label || 'Unknown'}${integrity.last_data_point ? ` · Last: ${new Date(integrity.last_data_point).toLocaleDateString()}` : ''}`}
                  color={(integrity.recency_score || 0) >= 80 ? '#2D7D46' : (integrity.recency_score || 0) >= 60 ? '#D9943B' : '#C6493A'}
                />
                <IntegrityGauge
                  label="Completeness"
                  value={integrity.completeness_pct || 0}
                  detail={integrity.completeness_details || 'N/A'}
                  color={(integrity.completeness_pct || 0) >= 90 ? '#2D7D46' : (integrity.completeness_pct || 0) >= 70 ? '#D9943B' : '#C6493A'}
                />
              </div>
            </div>

            {/* Data Provenance Chain */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <ShieldCheck size={14} className="text-brand-primary" />
                <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider">Data Provenance Chain</h3>
              </div>
              <div data-testid="provenance-section" className="bg-surface-default rounded-lg border border-border-subtle p-4">
                <div className="flex items-center overflow-x-auto pb-2">
                  {provenance.chain?.map((step, i) => (
                    <ProvenanceStep key={i} step={step} isLast={i === provenance.chain.length - 1} />
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border-subtle text-[9px] text-content-disabled">
                  <span>Source: <span className="font-mono text-brand-primary">{provenance.source_collection}</span></span>
                  <span>Records: <span className="font-mono">{provenance.record_count}</span></span>
                  <span>Evidence: <span className="font-mono">{provenance.evidence_count} docs</span></span>
                </div>
              </div>
            </div>

            {/* Data Breakdown Table */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <ChartPie size={14} className="text-brand-primary" />
                <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider">Data Breakdown ({breakdown.length} records)</h3>
              </div>
              {breakdown.length > 0 ? (
                <div data-testid="breakdown-table" className="bg-white border border-border-default rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0">
                        <tr className="bg-surface-muted">
                          {columns.map(col => (
                            <th key={col} className="text-left px-3 py-2 font-semibold text-content-secondary text-[9px] uppercase tracking-wider whitespace-nowrap">
                              {col.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle">
                        {breakdown.map((row, i) => (
                          <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                            {columns.map(col => (
                              <td key={col} className="px-3 py-2 text-content-primary whitespace-nowrap">
                                {typeof row[col] === 'number' ? row[col].toLocaleString() : (row[col] || '-')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-content-disabled text-center py-6 bg-surface-default rounded-lg">No breakdown data available</p>
              )}
            </div>
          </div>
        ) : (
          <div className="py-20 text-center text-sm text-content-disabled">Failed to load drill-down data</div>
        )}
      </div>
    </div>
  );
}
